import { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import { UNRATED } from 'src/pages/controls/lookupData';
import type { RiskRegisterFields } from 'src/pages/risks/types';
import { isOnLatestConfig } from 'src/ratings/configVersion';
import type { ResolvedRating } from 'src/ratings/useRiskRatingResolver';

import type { BubbleSizeEncoding } from './riskBubbleChartSettingsSchema';

/**
 * The size channel of every bubble while size encodes nothing. Equal size
 * bounds make Highcharts ignore `z`, so the value is immaterial; it is kept
 * positive so a fixed-size point can never be mistaken for a measured zero.
 */
export const FIXED_BUBBLE_Z = 1;

export interface RiskBubbleRating {
  label: string;
  /** Null for the unrated treatment, which has no position on the scale. */
  value: number | null;
  color?: string;
}

export interface RiskBubblePoint {
  id: string;
  name: string;
  likelihood: number;
  impact: number;
  /**
   * The size channel: the magnitude of the configured measure, never
   * negative, so a risk with nothing to measure sits at the smallest size
   * rather than vanishing. `FIXED_BUBBLE_Z` while size encodes nothing.
   */
  z: number;
  /** The measure as read, signed, for the tooltip. Null while size encodes nothing. */
  measure: number | null;
  /**
   * Residual is worse than inherent. Size carries only the magnitude of the
   * movement, so the direction has to travel separately (RSP-7292). Only ever
   * true under the movement encoding.
   */
  worse: boolean;
  rating: RiskBubbleRating;
}

interface ScoreSide {
  likelihood: number | null;
  impact: number | null;
  configId: string | null;
  rating: ResolvedRating | undefined;
}

/** Residual for Controlled, inherent for Uncontrolled — the side a chart plots. */
export const selectScoreSide = (
  risk: RiskRegisterFields,
  controlType: RiskAssessmentResultControlType
): ScoreSide =>
  controlType === RiskAssessmentResultControlType.Controlled
    ? {
        likelihood: risk.ControlledLikelihoodValue,
        impact: risk.ControlledImpactValue,
        configId: risk.ResidualConfigId,
        rating: risk.ControlledRatingOptionLatestOnly,
      }
    : {
        likelihood: risk.UncontrolledLikelihoodValue,
        impact: risk.UncontrolledImpactValue,
        configId: risk.InherentConfigId,
        rating: risk.UncontrolledRatingOptionLatestOnly,
      };

// A non-positive score is the unassessed sentinel the models persist, never a
// coordinate (SPEC §9a D-a) — the same test the heatmaps apply.
const isAssessed = (value: number | null): value is number =>
  value != null && value > 0;

interface AssessedSide {
  likelihood: number;
  impact: number;
}

const assessedOnLatestConfig = (
  side: ScoreSide,
  latestConfigId: string | null | undefined
): AssessedSide | undefined =>
  isAssessed(side.likelihood) &&
  isAssessed(side.impact) &&
  isOnLatestConfig(side.configId, latestConfigId)
    ? { likelihood: side.likelihood, impact: side.impact }
    : undefined;

/**
 * Children of each risk among the rows the viewer may see, keyed by parent
 * id. Counted over the widget's rows before its own filter is applied, so
 * filtering the chart to one tier does not zero the counts of the tier above.
 */
export const countChildRisks = (
  rows: readonly { Id: string; ParentRiskId?: string | null }[] | undefined
): ReadonlyMap<string, number> => {
  const counts = new Map<string, number>();
  for (const row of rows ?? []) {
    if (row.ParentRiskId) {
      counts.set(row.ParentRiskId, (counts.get(row.ParentRiskId) ?? 0) + 1);
    }
  }

  return counts;
};

export interface BubbleSizing {
  encoding: BubbleSizeEncoding;
  childCounts: ReadonlyMap<string, number>;
}

export const FIXED_BUBBLE_SIZING: BubbleSizing = {
  encoding: 'fixed',
  childCounts: new Map(),
};

type SizeChannel = Pick<RiskBubblePoint, 'z' | 'measure' | 'worse'>;

const FIXED_SIZE_CHANNEL: SizeChannel = {
  z: FIXED_BUBBLE_Z,
  measure: null,
  worse: false,
};

/** Nothing to measure: the smallest size, and still drawn. */
const UNMEASURED_SIZE_CHANNEL: SizeChannel = {
  z: 0,
  measure: null,
  worse: false,
};

const countChannel = (count: number): SizeChannel => ({
  z: count,
  measure: count,
  worse: false,
});

/**
 * How far the controls moved the risk, as the change in score
 * (likelihood × impact, the score every scoring model persists) from inherent
 * to residual. Positive when controls reduce the risk; negative when the
 * residual is worse than the inherent. Both sides must be assessed under the
 * current scoring config, or there is nothing comparable to measure.
 */
const movementChannel = (
  risk: RiskRegisterFields,
  latestConfigId: string | null | undefined
): SizeChannel => {
  const inherent = assessedOnLatestConfig(
    selectScoreSide(risk, RiskAssessmentResultControlType.Uncontrolled),
    latestConfigId
  );
  const residual = assessedOnLatestConfig(
    selectScoreSide(risk, RiskAssessmentResultControlType.Controlled),
    latestConfigId
  );
  if (!inherent || !residual) {
    return UNMEASURED_SIZE_CHANNEL;
  }
  const movement =
    inherent.likelihood * inherent.impact -
    residual.likelihood * residual.impact;

  return { z: Math.abs(movement), measure: movement, worse: movement < 0 };
};

export const measureBubbleSize = (
  risk: RiskRegisterFields,
  sizing: BubbleSizing,
  latestConfigId: string | null | undefined
): SizeChannel => {
  switch (sizing.encoding) {
    case 'fixed':
      return FIXED_SIZE_CHANNEL;
    case 'linkedControls':
      return countChannel(risk.LinkedControlCount);
    case 'childRisks':
      return countChannel(sizing.childCounts.get(risk.Id) ?? 0);
    case 'inherentToResidualMovement':
      return movementChannel(risk, latestConfigId);
  }
};

/**
 * One bubble per risk with an assessed score under the current scoring
 * config version, at its exact position. A risk the rule cannot rate still
 * has a position, so it takes the platform's unrated badge rather than
 * disappearing. Size carries the configured measure; the position never
 * depends on it.
 */
export const buildRiskBubblePoints = (
  risks: readonly RiskRegisterFields[] | undefined,
  controlType: RiskAssessmentResultControlType,
  latestConfigId: string | null | undefined,
  sizing: BubbleSizing = FIXED_BUBBLE_SIZING
): RiskBubblePoint[] =>
  (risks ?? []).flatMap((risk): RiskBubblePoint[] => {
    const side = selectScoreSide(risk, controlType);
    const assessed = assessedOnLatestConfig(side, latestConfigId);
    if (!assessed) {
      return [];
    }

    return [
      {
        id: risk.Id,
        name: risk.SequentialIdLabel
          ? `${risk.SequentialIdLabel}: ${risk.Title}`
          : risk.Title,
        likelihood: assessed.likelihood,
        impact: assessed.impact,
        ...measureBubbleSize(risk, sizing, latestConfigId),
        rating: side.rating ?? UNRATED,
      },
    ];
  });
